import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  createHseRiskCategory,
  deleteHseRiskCategory,
  fetchHseRiskCategories,
  updateHseRiskCategory,
} from "../../../api/hse/riskCategories";

export function HseRiskCategoriesEditor() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [newName, setNewName] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchHseRiskCategories();
      setCategories(response.data || []);
    } catch {
      toast.error(t("hse.settings.risk.categories_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (category) => {
    setBusyId(category.id);
    try {
      const saved = await updateHseRiskCategory(category.id, { active: !category.active });
      setCategories((prev) => prev.map((item) => (item.id === saved.id ? saved : item)));
    } catch {
      toast.error(t("hse.settings.risk.categories_save_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusyId("create");
    try {
      const saved = await createHseRiskCategory({
        name,
        position: categories.length + 1,
      });
      setCategories((prev) => [...prev, saved]);
      setNewName("");
      toast.success(t("hse.settings.risk.category_added"));
    } catch {
      toast.error(t("hse.settings.risk.categories_save_failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (category) => {
    if (category.system_default) return;
    setBusyId(category.id);
    try {
      await deleteHseRiskCategory(category.id);
      setCategories((prev) => prev.filter((item) => item.id !== category.id));
      toast.success(t("hse.settings.risk.category_deleted"));
    } catch {
      toast.error(t("hse.settings.risk.categories_save_failed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <h3 className="text-base font-semibold text-slate-900">
        {t("hse.settings.risk.categories_title")}
      </h3>
      <p className="mt-1 text-sm text-slate-600">
        {t("hse.settings.risk.categories_description")}
      </p>

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">{t("hse.settings.risk.categories_loading")}</p>
      ) : (
        <div className="mt-4 space-y-2">
          {categories.map((category) => (
            <div
              key={category.id}
              className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 px-3 py-2"
            >
              <label className="flex min-w-0 items-center gap-3">
                <input
                  type="checkbox"
                  checked={category.active === true}
                  disabled={busyId === category.id}
                  onChange={() => handleToggle(category)}
                  className="h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                />
                <span className="truncate text-sm font-medium text-slate-900">{category.name}</span>
                {category.system_default ? (
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-500">
                    {t("hse.settings.risk.category_system")}
                  </span>
                ) : null}
              </label>
              {!category.system_default ? (
                <button
                  type="button"
                  onClick={() => handleDelete(category)}
                  disabled={busyId === category.id}
                  className="rounded-full p-1.5 text-slate-400 hover:bg-white hover:text-rose-700"
                  aria-label={t("hse.settings.risk.category_delete")}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder={t("hse.settings.risk.category_name")}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={!newName.trim() || busyId === "create"}
          className="inline-flex items-center justify-center gap-1 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4" />
          {t("hse.settings.risk.category_add")}
        </button>
      </div>
    </section>
  );
}
